import { Button, Col, message, Row, Space, Tooltip } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import Axios from "axios";
import debounce from "lodash.debounce";
import { Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DividerLine from "../../../../components/lineSplit";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import _t from "../../../../lang/translate";
import { Vehicle } from "../../../../types/appTypes";
import format from "../../../../utilities/formatNumbers";
import FiltersForm from "./marketPlaceFilters";
import MarketPlaceTable from "./marketPlaceTable";
import { findEntries, inferEnrichment, requestEnrichment } from "./service";
import { Advert, CatalogWarning, CatalogModeration, Filters, PaginatedAdverts } from "./types";
import { mapMakeToBilinfo } from "./util";

export type AdvertMarketPlaceProps = {
  onSuccess: (values: OutputPayload) => void;
  onCancel: (errors?: any) => void;
  payload: InputPayload;
}
type InputPayload = {
  vehicle: Vehicle | null;
  preSelectedAdvertIds: Advert['id'][]
  filters: Filters | null;
  enrichmentQuality: "LOW" | "HIGH"
  newPriceWithoutEquipment: number | null;
  equipmentPrice: number | null;
};
type OutputPayload = {
  advertSelections?: Advert[];
  appliedFilters: Filters;
  didStartEnrichment: boolean;
};


const TODAY_ISO = new Date().toISOString()
function getInitialFilters(vehicle: Vehicle | null): Partial<Filters> {
  const regYear = vehicle?.firstRegDate ? new Date(vehicle?.firstRegDate).getFullYear() : null
  // let mileageRange = 0.1;
  const searchHint = vehicle?.searchHint;

  return {
    searchDate: TODAY_ISO,
    status: 0,
    type: String(vehicle?.typeId) ?? undefined,
    manufacturer: searchHint?.make ? mapMakeToBilinfo(searchHint?.make) : vehicle?.brand ? mapMakeToBilinfo(searchHint?.make || vehicle?.brand) : undefined,
    search: `${searchHint?.model ?? ""} ${searchHint?.variant ?? ""}`,
    priceType: 1,

    regYearFrom: regYear ?? undefined,
    regYearTo: regYear ?? undefined,
    // mileageFrom: vehicle?.mileage ? roundToNearest(vehicle.mileage * (1 - mileageRange), 3) : undefined, // Mileage +/- 10%
    // mileageTo: vehicle?.mileage ? roundToNearest(vehicle.mileage * (1 + mileageRange), 3) : undefined, // Mileage +/- 10%
    // gearbox: vehicle?.gearboxId && vehicle.gearboxId !== 127 ? vehicle.gearboxId : undefined,
  }
}

export type Pagination = {
  page: number;
  perPage: number;
}
export type QueryData = {
  filters: Filters;
  pagination: Pagination;
  sorter: SorterResult<Advert> | null
}
export default function AdvertMarketPlace({ onSuccess, payload, onCancel }: AdvertMarketPlaceProps) {
  const [saving, setSaving] = useState(false)
  const { vehicle, preSelectedAdvertIds, filters: previousAppliedFilters, enrichmentQuality, newPriceWithoutEquipment, equipmentPrice } = payload
  const [advertSelections, setAdvertSelections] = useState<Advert[]>([])
  const [moderation, setModeration] = useState<CatalogModeration>({})
  const handleWarningChanged = (warning: CatalogWarning) => setModeration(previous => ({ ...previous, [warning.key]: warning }))
  const [queryData, setQueryData] = useState<QueryData>(() => ({
    filters: previousAppliedFilters ?? getInitialFilters(vehicle),
    pagination: {
      page: 1,
      perPage: 50
    },
    sorter: null
  }))
  const setFilters = (action: React.SetStateAction<Filters>) => {
    setQueryData((prev) => {
      return {
        ...prev,
        filters: typeof action === 'function' ? action(prev.filters) : action
      }
    })
  }
  const [data, setData] = useState<PaginatedAdverts>({
    data: [],
    meta: {
      current_page: 1,
      per_page: 50,
      last_page: 1,
      total: 1,
      from: 1,
      links: [],
      path: "",
      to: 1
    }
  })
  const mountedRef = useRef(false)
  const prevFiltersRef = useRef<Filters | null>(null)
  const reqIdRef = useRef(0)
  const [loadingData, setLoadingData] = useState(false)

  const handleFetching = useCallback(async (nextQueryData: QueryData) => {
    const reqId = ++reqIdRef.current
    try {
      setLoadingData(true);

      const response = await findEntries(nextQueryData);
      if (!mountedRef.current) return;
      if (reqId !== reqIdRef.current) return;

      if (response.status === 202) {
        if ("message" in response.data.data) {
          message.loading(response.data.data.message);
        }
      } else {
        if ("meta" in response.data && typeof response.data.data !== "boolean") {
          setData(response.data);
          const incomingModeration = response.data.moderation;
          setModeration(previous => ({ ...previous, ...incomingModeration }));
        }
      }

      prevFiltersRef.current = nextQueryData.filters;
    } catch (error) {
      if (Axios.isCancel(error)) {
        console.log("Request canceled", error);
        return;
        // @ts-ignore
      } else if (error?.message === 'Network Error') {
        console.log("Network Error", error);
        return;
      }
      else {
        renderErrorMessage(error);
      }
    } finally {
      if (reqId !== reqIdRef.current) return;
      if (mountedRef.current) {
        setLoadingData(false);
      }
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce((nextQueryData: QueryData) => {
      handleFetching(nextQueryData);
    }, 450),
    [handleFetching]
  );

  useEffect(() => {
    debouncedFetch({
      ...queryData,
      pagination: {
        ...queryData.pagination,
        page: 1,
      }
    });
    setQueryData((prev) => ({ ...prev, pagination: { ...prev.pagination, page: 1 } }))
    return () => {
      debouncedFetch.cancel();
    };
  }, [
    debouncedFetch,
    queryData.filters.searchDate,
    queryData.filters.search,
    queryData.filters.regYearFrom,
    queryData.filters.regYearTo,
    queryData.filters.mileageFrom,
    queryData.filters.mileageTo,
    queryData.filters.priceFrom,
    queryData.filters.priceTo,
    queryData.filters.horsePowerFrom,
    queryData.filters.horsePowerTo,
    queryData.filters.engineSizeFrom,
    queryData.filters.engineSizeTo,
  ]);

  // No debounce on these fields
  useEffect(() => {
    debouncedFetch.cancel();
    handleFetching({
      ...queryData,
      pagination: {
        ...queryData.pagination,
        page: 1,
      }
    });
    setQueryData((prev) => ({ ...prev, pagination: { ...prev.pagination, page: 1 } }))
  }, [
    queryData.filters.status,
    queryData.filters.type,
    queryData.filters.manufacturer,
    queryData.filters.model,
    queryData.filters.variant,
    queryData.filters.body,
    queryData.filters.fuel,
    queryData.filters.gearbox,
    queryData.filters.priceType,

    handleFetching,
    debouncedFetch,
  ]);

  useEffect(() => {
    debouncedFetch.cancel();
    handleFetching(queryData);
  }, [
    queryData.sorter?.column,
    queryData.sorter?.order,
    queryData.pagination?.page,
    queryData.pagination?.perPage,

    handleFetching,
    debouncedFetch,
  ]);



  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  async function handleSave() {
    try {
      setSaving(true)
      let didStartEnrichment = true;

      const eCode = vehicle?.motorAtlasId;
      if (!eCode) {
        renderErrorMessage("Kan ikke berige! Modelkatalog ikke valgt.")
        didStartEnrichment = false;
      } else {
        if (enrichmentQuality === 'HIGH') {
          await requestEnrichment({
            payloads: advertSelections?.map(ad => ({
              advertId: String(ad.id),
              identifierECode: eCode,
              priceDate: vehicle?.firstRegDate,
            })) ?? []
          })
        } else {
          await inferEnrichment({
            payloads: advertSelections?.map(ad => ({
              advertId: String(ad.id),
              identifierECode: eCode,
              priceDate: vehicle?.firstRegDate,
            })) ?? []
          })
        }
      }
      onSuccess({
        advertSelections,
        appliedFilters: { ...queryData.filters },
        didStartEnrichment: didStartEnrichment
      })
      // Body here
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    onSuccess({
      advertSelections: [],
      appliedFilters: { ...queryData.filters },
      didStartEnrichment: false,
    })
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Row className="mb-1 flex-0" align="middle" justify="space-between">
        <Col>
          <h2 className="mb-0">{_t("car_market")}</h2>
        </Col>
        <Col>
          <span>
            <Tooltip placement="bottom" title={_t("new_price", 'without_equipments')}>
              {format.price(newPriceWithoutEquipment)}{' '}&bull;{' '}
            </Tooltip>
            <Tooltip placement="bottom" title={_t("equipment_price")}>
              {format.price(equipmentPrice)}{' '}&bull;{' '}
            </Tooltip>
            {vehicle?.brand} {vehicle?.model}
            {' '}&bull; {format.date(vehicle?.firstRegDate)}
            {' '}&bull; {format.milage(vehicle?.mileage)}
            {vehicle?.horsePower && (<>
              {' '}&bull; {vehicle?.horsePower} HK
            </>)}
            {' '}&bull; {format.emission(vehicle?.emissionAmount)}
            {vehicle?.batteryCapacity && (<>
              {' '}&bull; {format.batteryCapacity(vehicle?.batteryCapacity)}
            </>)}
            {vehicle?.gearbox && (
              <span title={_t('gearbox')}>
                {' '}&bull; {vehicle?.gearbox}
              </span>
            )}
            {vehicle?.body && (
              <span title={_t('trim')}>
                {' '}&bull; {vehicle?.body}
              </span>
            )}
          </span>
        </Col>
      </Row>

      <Row gutter={[16, 16]} wrap={false} className="flex-1 pb-2">
        <Col flex={"1 1 0%"} >
          <div className="border-light border-rounded overflow-hidden" style={{ position: "relative", width: "100%", height: "100%", }}>
            <div style={{
              position: "absolute", inset: 0,
            }}>
              <MarketPlaceTable data={data} preSelectedAdvertIds={preSelectedAdvertIds}
                moderation={moderation} onWarningChanged={handleWarningChanged}
                queryData={queryData} setQueryData={setQueryData} vehicle={vehicle}
                advertSelections={advertSelections} setAdvertSelections={setAdvertSelections}
              />
            </div>
          </div>
        </Col>
        <Col flex={"0 0 388px"} >
          <div className="p-1 border-light border-radius">
            <FiltersForm filters={queryData.filters} setFilters={setFilters} data={data} setData={setData} loadingData={loadingData} />
          </div>
          <div className="mt-1 d-flex" style={{ gap: 8 }}>
            <Button className="flex-1" onClick={handleCancel}>{_t("close")}</Button>
            <Button className="flex-1" onClick={handleSave} type="primary" loading={saving}>{_t("save_and_use")}</Button>
          </div>
        </Col>
      </Row>
    </div>
  )
}
